/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import type { Interpolation } from '@emotion/css';
import { useUniversalNavContext } from '../../hooks';

import { emitUserInteractionEvent } from '../../utils/avroEvents';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { Animation, Colors, useClickOutside } from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';

import SkipLink from '../SkipLink';
import { UniversalTopbarLinks, skipLinkHref } from '../../constants';

import AccountActions from './AccountActions';
import SearchToggler from './SearchToggler';
import UniversalTopbarLink from './UniversalTopbarLink';
import SearchForm from './SearchForm';
import type { Props as SearchFormProps } from './SearchForm';

import type { UniversalNavLinkType } from '../../constants';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../constants';

export type Props = {
  /*
   * Indicates whether search fills the entire topbar or not.
   */
  isSearchFullWidth: boolean;
  /**
   * An array of links to be rendered inside the main navigation.
   */
  links?: Array<UniversalNavLinkType>;
  /**
   * Zapier logo label for link
   */
  logoAriaLabel: string;
  /**
   * Zapier logo link
   */
  logoHref: string;
  /**
   * Optional handler for when anywhere on the top nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when somewhere outside of the nav is clicked.
   */
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  /**
   * Zapier logo link callback
   */
  onLogoClick?: () => void;
  /**
   * Renders an account menu if provided. This overrides the default account actions menu (Log in, Sign up)
   */
  renderAccountMenu: (props: Props) => ReactNode;
  /**
   * Renders notifications component if provided
   */
  renderNotifications: (props: Props) => ReactNode;
  /**
   * Renders search component, or prevents it from rendering
   */
  renderSearchForm: (props: SearchFormProps) => ReactNode;
  /**
   * Render the sidebar component if provided
   */
  renderSidebar?: (props: Props) => ReactNode;
  /**
   * Currently selected link inside links
   */
  selectedHref: string;
  /**
   * Indicates whether the logo should render.
   */
  shouldRenderLogo: boolean;
  /**
   * Height of the topbar. Passed in as a prop since other layouts
   * may need to know its height. Typically sourced from context object.
   *
   * Related to `props.size`, with each serving different purposes.
   */
  height: number;
  /**
   * Size of the topbar which adjusts its height, padding, etc.
   *
   * Related to `props.height`, with each serving different purposes.
   */
  size: 'small' | 'medium';
  /**
   * Anchor to the main content area of the page.
   * **The page must contain a node with the `id` in this prop!**
   */
  skipLinkHref: string;
};

type LinksProps = Props & { isSearching: boolean };

const Styles = labelStyles('UniversalTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `${Colors.neutral100}`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.neutral300}`,
    },
  ],
  header: (props: Props, { hasSidebar }: { hasSidebar: boolean }) => [
    {
      alignItems: 'stretch',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      width: '100%',
      backgroundColor: `${Colors.neutral100}`,
    },
    !hasSidebar &&
      withModifier('hasMaxWidth', {
        maxWidth: 1200,
      }),
    withModifier(
      `${props.size}-${props.height}`,
      {
        small: {
          gap: 5,
          height: props.height,
          padding: '0 10px',
        },
        medium: {
          gap: '67px',
          height: props.height,
          padding: '0 20px',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: hasSidebar ? 10 : 20,
        },
      }[props.size]
    ),
  ],
  group: (props: Props, { gap }: { gap?: number } = {}) => [
    {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
    },
    withModifier(
      props.size,
      {
        small: { gap: 15 },
        medium: { gap: 20 },
      }[props.size]
    ),
    !!gap &&
      withModifier(`gap-${gap}`, {
        gap,
      }),
  ],
  mainNavigationContent: (props: Props) => [
    {
      display: 'flex',
      alignItems: 'stretch',
    },
    !props.isSearchFullWidth &&
      withModifier('offsetParent', {
        position: 'relative',
      }),
  ],
  searchForm: (props: Props, { isSearching }: { isSearching: boolean }) => [
    {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: Animation.transitionValue,
      opacity: 0,
      pointerEvents: 'none',
      transform: 'scaleX(0)',
    },
    isSearching &&
      withModifier('searching', {
        opacity: 1,
        pointerEvents: 'auto',
        transform: 'scaleX(1)',
      }),
    props.isSearchFullWidth &&
      withModifier('fullWidth', {
        backgroundColor: `${Colors.neutral100}`,
        padding: '0 10px',
      }),
  ],
  searchToggler: ({ isSearching }: { isSearching: boolean }) => [
    {
      marginRight: -7,
      transition: Animation.transitionValue,
    },
    isSearching &&
      withModifier('hidden', {
        opacity: 0.2,
        pointerEvents: 'none',
      }),
  ],
  navLinks: {
    lineHeight: 1.5,
  },
  navLinksList: (props: LinksProps) => [
    {
      display: 'grid',
      gridGap: 30,
      ...(props.links && {
        gridTemplateColumns: `repeat(${props.links.length}, max-content)`,
      }),
    },
    props.isSearching &&
      withModifier('searching', {
        opacity: 0,
      }),
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: LinksProps) => {
  const links = props.links?.map((link) => (
    <li key={link.text}>
      <UniversalTopbarLink
        href={link.href}
        isSelected={props.selectedHref === link.href}
        submenu={link.submenu}
      >
        {link.text}
      </UniversalTopbarLink>
    </li>
  ));
  if (!links) {
    return null;
  }
  return (
    <nav aria-label="Navigation Links" css={Styles.navLinks as Interpolation}>
      <ul css={Styles.navLinksList(props) as Interpolation}>{links}</ul>
    </nav>
  );
};

/**
 * @private
 * Renders universal navigation top bar with Zapier logo, search,
 * links and account actions.
 */
const UniversalTopbar = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const sidebar = props.renderSidebar && props.renderSidebar(props);
  const logoSize = {
    small: { width: 81, height: 22 },
    medium: { width: 81, height: 22 },
  }[props.size];
  // Used to tie search `input` to its search toggler `label`.
  const searchInputId = 'universal-nav-app-search';
  // Track whether the user is currently searching.
  const [isSearching, setIsSearching] = useState(false);
  const onCloseSearch = () => {
    setIsSearching(false);
  };
  const searchForm = props.renderSearchForm({
    isSearching,
    searchInputId,
    onClose: onCloseSearch,
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });

  const { links = UniversalTopbarLinks } = props;

  return (
    // onClick here exists to support behaviour logic might needed by other components of the Nav.
    //  E.g; Closing the sidebar opened by Topbar if another element on Topbar is clicked.
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header(props, { hasSidebar: !!sidebar })}
        data-testid="universalTopbar"
        onClick={props.onClick}
      >
        <div css={Styles.group(props, { gap: 17 }) as Interpolation}>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref) as string}
                onClick={props.onLogoClick}
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink as Interpolation}>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent(props) as Interpolation}>
          {links.length > 0 && (
            <Links {...props} isSearching={isSearching} links={links} />
          )}
          {!!searchForm && (
            <div
              aria-label="Search apps"
              css={Styles.searchForm(props, { isSearching })}
              onBlur={() => {
                setIsSearching(false);
              }}
              onFocus={() => {
                setIsSearching(true);
                emitUserInteractionEvent({
                  interaction_name: searchInputId,
                  event_action: 'in_view',
                  option_selected: 'Universal Nav Search opened',
                });
              }}
              role="form"
            >
              {searchForm}
            </div>
          )}
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group(props) as Interpolation}
          role="form"
        >
          {!!searchForm && (
            <div
              css={Styles.searchToggler({ isSearching }) as Interpolation}
              data-testid="searchToggler"
            >
              <SearchToggler
                id={searchInputId}
                isSearching={false}
                label="Search apps…"
              />
            </div>
          )}
          {props.renderNotifications(props)}
          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

UniversalTopbar.defaultProps = {
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  renderSearchForm: (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  selectedHref: '',
  shouldRenderLogo: true,
  height: UNIVERSAL_TOPBAR_HEIGHT,
  size: 'medium',
  skipLinkHref,
};

export default UniversalTopbar;
